import { StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { type AccountInfo, EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { authEnabled, msalConfig } from "./auth/config";
import { getDocFields, loadOverlay } from "./lib/overlay";
import { applyConfig, deepMerge, getAppConfig, loadConfig, useAppConfig } from "./lib/appConfig";
import { verifyMagicLink } from "./components/Register";
import { consumeQlink } from "./lib/askQuota";
import { initRedliner } from "./integrations/redliner";
import { router } from "./router";
import { StaticResume } from "./pages/StaticResume";
import "./index.css";

const root = createRoot(document.getElementById("root")!);
const render = (tree: ReactNode) => root.render(<StrictMode>{tree}</StrictMode>);

initRedliner(window.RedLinerSDK, __APP_VERSION__, import.meta.env.DEV);

// Reactive master switch: an admin publishing interactive=false (config-active) flips the live
// session to the flat résumé without a reload. The baked-false case never reaches here — boot()
// renders StaticResume directly, before any API/auth init.
function AppRoot() {
  const cfg = useAppConfig();
  return cfg.interactive ? <RouterProvider router={router} /> : <StaticResume />;
}

async function boot() {
  // The master interactivity flag lives in the baked config (app-config.json). Read it FIRST —
  // before any overlay/API/auth call — so a disabled site makes zero backend or AI requests.
  await loadConfig();
  if (!getAppConfig().interactive) {
    document.documentElement.setAttribute("data-theme", "light"); // the flat résumé is always light
    render(<StaticResume />);
    return;
  }

  // If the visitor arrived via a magic link, confirm it (best-effort, cleans the URL).
  void verifyMagicLink();
  // If the visitor arrived via an Ask-AI quota link (?qlink), redeem it for more questions.
  void consumeQlink().then((r) => {
    if (r?.granted) window.dispatchEvent(new CustomEvent("quota-granted", { detail: { granted: r.granted } }));
  });
  // Apply live admin content edits before first paint (best-effort).
  await loadOverlay();
  // Layer a published admin config (config-active) over the build-time baseline, if present.
  try {
    const raw = getDocFields("config-active").config;
    if (raw) applyConfig(deepMerge(getAppConfig(), JSON.parse(raw)));
  } catch {
    // best-effort — fall back to the build-time config
  }

  if (!authEnabled) {
    render(<AppRoot />);
    return;
  }

  // MSAL must be initialize()'d before render (msal-browser v3+).
  const msal = new PublicClientApplication(msalConfig);
  await msal.initialize();
  const accounts = msal.getAllAccounts();
  if (accounts.length > 0 && !msal.getActiveAccount()) {
    msal.setActiveAccount(accounts[0]);
  }
  msal.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload && "account" in event.payload) {
      msal.setActiveAccount((event.payload as { account: AccountInfo }).account);
    }
  });

  // Cross-tab auth reconciliation: the MSAL cache is shared via localStorage, but each tab keeps its
  // own MSAL React state. When the signed-in account changes in another tab (sign-in or sign-out),
  // reload so this tab reflects it instead of showing a stale signed-in/signed-out view.
  window.addEventListener("storage", (e) => {
    if (e.key && e.key.includes("account.keys")) window.location.reload();
  });

  render(
    <MsalProvider instance={msal}>
      <AppRoot />
    </MsalProvider>,
  );
}

void boot();
